import { GEOMETRY_EPSILON, segmentTouchesRectangle, insideRectangle } from '../navigation/geometry.js';
import type { MapDefinition, Obstacle, Point } from './world-types.js';
import { cliffHides } from './cliffs.js';
import { terrainHeight } from './terrain.js';

/**
 * Solid scenery stops vision. Water is flat and anything under a metre thick (fence panels) is see-through.
 * Cliffs are judged from where the viewer stands instead (`cliffHides`), however thin their steep band.
 */
export const blocksSight = (obstacle: Obstacle) => obstacle.kind !== 'water' && obstacle.kind !== 'bush' && (obstacle.kind === 'cliff' || Math.min(obstacle.width, obstacle.depth) >= 1);

/** Sight-blocking obstacles whose bounds touch the vision circle around `source`, including cliffs that rise above it. */
export function obstaclesNear(map: MapDefinition, source: Point, radius: number): Obstacle[] {
  let eye: number | undefined;
  return map.obstacles.filter(o => blocksSight(o) && o.x + o.width / 2 >= source.x - radius && o.x - o.width / 2 <= source.x + radius
    && o.z + o.depth / 2 >= source.z - radius && o.z - o.depth / 2 <= source.z + radius
    && (o.kind !== 'cliff' || cliffHides(o, eye ??= terrainHeight(map, source.x, source.z))));
}

/**
 * Whether `source` has a clear line to `point`. An obstacle containing `point` does not hide itself
 * (you see its near face), but it hides everything behind it. Shared by the server's exploration and the client's veil.
 */
export function lineOfSight(source: Point, point: Point, blockers: readonly Obstacle[]): boolean {
  for (const obstacle of blockers) {
    if (insideRectangle(point, obstacle) || insideRectangle(source, obstacle)) continue;
    if (segmentTouchesRectangle(source, point, obstacle)) return false;
  }
  return true;
}

const SECTORS = 512, SECTOR = 2 * Math.PI / SECTORS, SLACK = 1e-6;
const sectorOf = (angle: number) => Math.floor((angle + Math.PI) / SECTOR) % SECTORS;

/**
 * `lineOfSight` from one source to many points, with the same answers: each blocker is filed under the bearings it spans from the
 * source, so a point is tested only against blockers in its own direction that are no farther than it. A cave's view holds a
 * couple of hundred blockers, most of them thin rim walls each covering a few degrees.
 */
export class SightLines {
  private readonly sectors: number[][] = Array.from({ length: SECTORS }, () => []);
  private readonly blockers: Obstacle[] = [];
  /** Squared distance from the source to each blocker's nearest edge. */
  private readonly nearest: number[] = [];

  constructor(private readonly source: Point, blockers: readonly Obstacle[]) {
    for (const obstacle of blockers) {
      // An obstacle round the viewer hides nothing.
      if (insideRectangle(source, obstacle)) continue;
      // The bounds `segmentTouchesRectangle` tests against, with room for rounding.
      const halfWidth = obstacle.width / 2 + GEOMETRY_EPSILON + SLACK, halfDepth = obstacle.depth / 2 + GEOMETRY_EPSILON + SLACK;
      const dx = Math.max(0, Math.abs(source.x - obstacle.x) - halfWidth), dz = Math.max(0, Math.abs(source.z - obstacle.z) - halfDepth);
      const index = this.blockers.push(obstacle) - 1;
      const nearest = Math.max(0, Math.hypot(dx, dz) - SLACK);
      this.nearest.push(nearest * nearest);
      if (!dx && !dz) { for (const sector of this.sectors) sector.push(index); continue; }
      // Seen from outside, a rectangle spans less than half a turn: its corners' bearings round the bearing of its centre.
      const centre = Math.atan2(obstacle.z - source.z, obstacle.x - source.x);
      let low = Infinity, high = -Infinity;
      for (const [cx, cz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        let turn = Math.atan2(obstacle.z + cz * halfDepth - source.z, obstacle.x + cx * halfWidth - source.x) - centre;
        if (turn > Math.PI) turn -= 2 * Math.PI; else if (turn < -Math.PI) turn += 2 * Math.PI;
        low = Math.min(low, turn); high = Math.max(high, turn);
      }
      const first = Math.floor((centre + low - SLACK + Math.PI) / SECTOR), last = Math.floor((centre + high + SLACK + Math.PI) / SECTOR);
      for (let sector = first; sector <= last; sector++) this.sectors[(sector % SECTORS + SECTORS) % SECTORS].push(index);
    }
  }

  visible(point: Point): boolean {
    const dx = point.x - this.source.x, dz = point.z - this.source.z, sector = this.sectors[sectorOf(Math.atan2(dz, dx))];
    if (!sector.length) return true;
    const distance = dx * dx + dz * dz;
    for (const index of sector) {
      if (this.nearest[index] > distance) continue;
      const obstacle = this.blockers[index];
      if (!insideRectangle(point, obstacle) && segmentTouchesRectangle(this.source, point, obstacle)) return false;
    }
    return true;
  }
}
